import { useState, useCallback, useMemo } from 'react';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { gql } from 'graphql-tag';
import { useDialogState } from '~/modules/common/hooks';
import { useMeContext } from '~/modules/me';
import { uploadFile } from '~/modules/attachments/uploader';
import { FILE_SOURCE_URI } from '~/modules/attachments/uploader/uris';
import useOnFileUpload from '~/modules/attachments/AttachmentsBlock/hooks/useOnFileUpload';
import { clearUserSkillNudgesCache } from '../Skills/hooks';
import { clearUserProfileCertificateNudgesCache } from '../Certificates/hooks';

export const UPLOAD_RESUME_MUTATION = gql`
  mutation updateResume($input: UpdateResumeInput!) {
    updateResume(input: $input) {
      userUri
      resume {
        uploadUri
      }
    }
  }
`;

export const DELETE_RESUME_MUTATION = gql`
  mutation deleteResume($input: DeleteResumeInput!) {
    deleteResume(input: $input) {
      userUri
    }
  }
`;

export const USER_RESUME_QUERY = gql`
  query userResume($userUri: String!) {
    user(id: $userUri) {
      id
      resume {
        linkName
        linkUri
        fileSize
        fileName
        type
        uploadUri
        fileExtension
        uploadedOn
        uploadedBy {
          id
          uri
          displayText
        }
      }
    }
  }
`;

export const useGetResume = ({ userUri }) => {
  const { data, loading: isLoading, refetch } = useQuery(USER_RESUME_QUERY, {
    variables: { userUri },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });

  return {
    data,
    isLoading,
    refetch
  };
};

const clearUserNudgesCache = ({ cache, userUri }) => {
  clearUserSkillNudgesCache({ cache, userUri });
  clearUserProfileCertificateNudgesCache({ cache, userUri });
};

export const useResumeHandlers = ({
  userUri,
  customMetadataFn,
  enabledSourceUris = [FILE_SOURCE_URI]
}) => {
  const me = useMeContext();
  const apolloClient = useApolloClient();
  const refetchQueries = useMemo(
    () => [
      {
        query: USER_RESUME_QUERY,
        variables: {
          userUri
        }
      }
    ],
    [userUri]
  );

  const {
    open: isDialogOpen,
    openDialog: onDialogOpen,
    closeDialog: onDialogClose
  } = useDialogState();

  const [isUploading, setIsUploading] = useState(false);

  const { data, isLoading, refetch } = useGetResume({
    userUri
  });

  const [uploadUserResume] = useMutation(UPLOAD_RESUME_MUTATION, {
    onCompleted: refetch,
    refetchQueries
  });

  const [deleteUserResume] = useMutation(DELETE_RESUME_MUTATION, {
    onCompleted: refetch,
    refetchQueries
  });

  const linkResumeToUser = useCallback(
    ({ attachments }) => {
      uploadUserResume({
        variables: {
          input: {
            userUri,
            resume: attachments[0]
          }
        },
        refetchQueries
      });
    },
    [uploadUserResume, userUri, refetchQueries]
  );

  const [error, setError] = useState(null);

  const onDone = useCallback(() => {
    setIsUploading(false);
    onDialogClose();
    clearUserNudgesCache({ cache: apolloClient.cache, userUri });
  }, [onDialogClose, apolloClient.cache, userUri]);

  const uploadFilesCallback = files =>
    uploadFile({
      servicesUrl: me.servicesUrl,
      attachToUri: userUri,
      onDone,
      setError,
      linkAttachmentsToObject: linkResumeToUser,
      customMetadataFn
    })(files);

  const onFileUpload = useOnFileUpload({
    setIsUploading,
    uploadFilesCallback
  });

  const deleteResume = useCallback(() => {
    deleteUserResume({
      variables: {
        input: {
          userUri
        }
      },
      refetchQueries,
      optimisticResponse: { deleteResume: { userUri } },
      update: cache => clearUserNudgesCache({ cache, userUri })
    });
  }, [deleteUserResume, userUri, refetchQueries]);

  return {
    resume: data?.user?.resume,
    isDialogOpen,
    onDialogOpen,
    onDialogClose,
    onFileUpload,
    isUploading,
    isLoading,
    error,
    deleteResume,
    enabledSourceUris
  };
};

export default useResumeHandlers;
